/** @jsxImportSource @emotion/react */
/* eslint-disable jsx-a11y/anchor-is-valid */
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useTheme } from '@emotion/react';
import CheckBox from '@CheckBox';
import IconTileCheckBox from '@IconTileCheckBox';
import ListLayout from '@ListLayout';
import TileLayout from '@TileLayout';
import { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  clearConstraintsValidations,
  clearSelectedOptionsAndValue,
  selectOptionAndUpdateValue,
  setComponentValidationStatus,
  setIsCheckedByDefault,
  updateOtherValueAndValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import QuestionText from '../QuestionText/QuestionText';
import makeCheckboxInputStyles from './makeCheckboxInputStyles';
import {
  isIconForEveryOption,
  swapS3DomainWithPublicAssetsDomain,
  getIconHeight,
} from '../__helpers__/component.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import useGetOptionHeights from '../../Hooks/useGetOptionHeights/useGetOptionHeights';

const CheckboxInput = ({ componentId, subheading, iFrameId, formId }) => {
  const dispatch = useDispatch();
  const [s3EnvDomain, setS3EnvDomain] = useState(
    process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL
  );

  const components = useSelector((store) => store.components);
  const supplierMatches = useSelector((store) => store.supplierMatches);
  const showSmartMatchMeter = useSelector(
    (store) => store.features.showSmartMatchMeter
  );
  const showSmartMatchMeterV2 = useSelector(
    (store) => store.features.showSmartMatchMeterV2
  );
  const component = components[`component_${componentId}`];
  const { options, selectedOptions, otherValue } = component.input;
  const { text, forceListLayout } = component.component;
  const theme = useTheme();
  const styles = makeCheckboxInputStyles(theme);

  const optionsWithoutTextLinks = options.filter(
    (option) => !option.display_as_text_link
  );
  const textLinkOption = options.find((option) => option.display_as_text_link);

  const allOptionsHaveIcons = isIconForEveryOption(optionsWithoutTextLinks);
  const isTiled = allOptionsHaveIcons && !forceListLayout;
  const Layout = isTiled ? TileLayout : ListLayout;

  // Use the custom hook for height measurement
  const { tallestOptionHeight, optionRefs } = useGetOptionHeights({
    options: optionsWithoutTextLinks,
    allOptionsHaveIcons,
    componentPage: component?.component?.page,
    isTiled,
  });

  const checkboxExperimentName = useGetExperimentName({
    componentId,
    componentName: 'CheckBox',
  });
  const iconCheckboxExperimentName = useGetExperimentName({
    componentId,
    componentName: 'IconTileCheckBox',
  });
  const layoutExperimentName = useGetExperimentName({
    componentId,
    componentName: isTiled ? 'TileLayout' : 'ListLayout',
  });

  const columnCount = component?.component?.additionalSettings?.column_count;

  const isInitiallyChecked = (option) => {
    const optionId = String(option.id);
    if (option.is_checked_by_default) {
      batch(() => {
        dispatch(
          selectOptionAndUpdateValue({
            componentId,
            optionId: option.id,
          })
        );
        dispatch(
          setComponentValidationStatus({
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        dispatch(
          setIsCheckedByDefault({
            componentId,
            optionId: option.id,
            isChecked: false,
          })
        );
      });
      return true;
    }
    let checked = selectedOptions?.includes(optionId);
    if (checked && optionId === 'other-option' && otherValue === '') {
      checked = false;
    }

    return checked;
  };

  const handleClick = ({ id, selected }) => {
    batch(() => {
      dispatch(clearConstraintsValidations({ componentId }));
      dispatch(
        selectOptionAndUpdateValue({
          componentId,
          optionId: id,
          selected,
        })
      );

      if (id !== 'other-option') {
        batch(() => {
          dispatch(
            setComponentValidationStatus({
              componentId,
              iFrameId,
              formId,
            })
          );
          dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        });
      } else if (id === 'other-option' && otherValue) {
        batch(() => {
          dispatch(
            setComponentValidationStatus({
              componentId,
              iFrameId,
              formId,
            })
          );
          dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        });
      }
    });
  };

  const handleChange = ({ id, value }) => {
    batch(() => {
      dispatch(
        clearConstraintsValidations({ componentId: component.component.id })
      );
      dispatch(updateOtherValueAndValue({ componentId, optionId: id, value }));
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const handleTextLinkClick = (event) => {
    event.preventDefault();
    batch(() => {
      dispatch(clearSelectedOptionsAndValue({ componentId }));
      dispatch(clearConstraintsValidations({ componentId }));
      dispatch(
        selectOptionAndUpdateValue({
          componentId,
          optionId: String(textLinkOption.id),
        })
      );
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    });
  };

  const handleTextLinkKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleTextLinkClick(event);
    }
  };

  useEffect(() => {
    if (s3EnvDomain !== process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL) {
      setS3EnvDomain(process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL);
    }
  }, [s3EnvDomain]);

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
          supplierMatches,
          showSmartMatchMeter,
          showSmartMatchMeterV2,
        })}
        subheading={renderMustache({
          template: subheading,
          components,
          currentComponent: component,
          iFrameId,
          formId,
          supplierMatches,
          showSmartMatchMeter,
          showSmartMatchMeterV2,
        })}
      />
      <Box marginTop={theme.mvf.spacing.medium} data-testid="checkboxContainer">
        <Layout
          experimentName={layoutExperimentName}
          columns={isTiled ? undefined : columnCount}
        >
          {optionsWithoutTextLinks.map((option) => {
            const { value, key, id, subtext } = option;
            const optionIdString = String(id);
            const isOtherOption = optionIdString === 'other-option';
            const strippedUrl = swapS3DomainWithPublicAssetsDomain(
              option.icon_url,
              process.env.NEXT_PUBLIC_ASSETS_URL
            );
            const Icon = allOptionsHaveIcons ? (
              <img
                style={{
                  maxWidth: '100%',
                  height: getIconHeight(isTiled, columnCount),
                }}
                src={strippedUrl}
                alt="optionImage"
              />
            ) : undefined;
            const optionText = isOtherOption ? key : value;
            const optionSubtext = isOtherOption
              ? component?.component?.additionalSettings?.other_option_subtext
              : subtext;

            const optionComponent = isTiled ? (
              <IconTileCheckBox
                key={optionIdString}
                id={optionIdString}
                isOtherOption={isOtherOption}
                text={optionText}
                otherInputValue={isOtherOption ? otherValue : undefined}
                selected={isInitiallyChecked(option)}
                onChange={handleChange}
                onClick={handleClick}
                Icon={Icon}
                experimentName={iconCheckboxExperimentName}
                subtext={optionSubtext}
                minHeight={tallestOptionHeight}
              />
            ) : (
              <CheckBox
                Icon={Icon}
                id={optionIdString}
                isOtherOption={isOtherOption}
                experimentName={checkboxExperimentName}
                key={optionIdString}
                onChange={handleChange}
                onClick={handleClick}
                otherInputValue={isOtherOption ? otherValue : undefined}
                selected={isInitiallyChecked(option)}
                text={optionText}
                smallIcon={columnCount > 1 && !isTiled}
                subtext={optionSubtext}
                minHeight={tallestOptionHeight}
              />
            );

            return (
              <div
                key={optionIdString}
                style={{
                  height: '100%',
                }}
                ref={(el) => {
                  optionRefs.current[optionIdString] = el;
                }}
              >
                {optionComponent}
              </div>
            );
          })}
        </Layout>
        {textLinkOption && (
          <Box css={styles.textLinkContainer} data-testid="textLinkOption">
            <Link
              component="button"
              underline="always"
              onClick={handleTextLinkClick}
              onKeyDown={handleTextLinkKeyDown}
              css={styles.textLink}
            >
              {textLinkOption.value}
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
};

export default CheckboxInput;
